import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { submitContactForm } from 'shared/api/api';

const ContactPage = () => {
    const { t } = useTranslation('common');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

    const people = [
        {
            name: 'Peter Thomsen',
            role: t('contact.sections.team.members.propertyConsultant'),
            imageUrl: '/peter-thomsen.png',
            bio: t('contact.sections.team.members.experience.sales'),
            email: 'peter.thomsen@infinitynewbuilds.com'
        },
        {
            name: 'Peter Aaskov',
            role: t('contact.sections.team.members.propertyConsultant'),
            imageUrl: '/peter-aaskov.png',
            bio: t('contact.sections.team.members.experience.development'),
            email: 'peter.aaskov@infinitynewbuilds.com'
        },
    ];

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setIsSubmitting(true);
            setSubmitStatus(null);
            
            // Submit the form data to the API
            await submitContactForm(formData);
            
            // Clear the form and show success message
            setFormData({
                name: '',
                email: '',
                phone: '',
                message: ''
            });
            
            setSubmitStatus('success');
        } catch (error) {
            console.error('Error submitting contact form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        
        // Clear status when user starts typing again
        if (submitStatus) {
            setSubmitStatus(null);
        }
    };

    return (
        <div className="bg-white">
            {/* Hero Section */}
            <div className="relative bg-sky-600 h-96 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/50" />
                <div className="relative z-10 text-center">
                    <img
                        src="/white-logo-square-inb.png"
                        alt={t('contact.hero.title')}
                        className="mx-auto h-56 object-contain mb-6"
                    />
                </div>
            </div>

            {/* Contact Info Section */}
            <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-gray-50 p-8 rounded-lg">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">{t('contact.sections.visit.title')}</h2>
                        <div className="space-y-6">
                            <div className="flex items-start">
                                <MapPin className="mt-1 h-5 w-5 text-sky-600" />
                                <div className="ml-4">
                                    <p className="text-lg font-medium text-gray-900">{t('contact.sections.visit.office.title', 'Benalmádena')}</p>
                                    <p className="mt-1 text-gray-600">
                                        {t('contact.sections.visit.office.address.line1', 'C. Quilla, 17, 2 16, 29630')}<br />
                                        {t('contact.sections.visit.office.address.line2', 'Benalmádena, Málaga')}<br />
                                        {t('contact.sections.visit.office.address.line3', 'Spain')}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <Clock className="mt-1 h-5 w-5 text-sky-600" />
                                <div className="ml-4">
                                    <p className="text-lg font-medium text-gray-900">{t('contact.sections.visit.hours.title')}</p>
                                    <p className="mt-1 text-gray-600">
                                        {t('contact.sections.visit.hours.weekdays')}<br />
                                        {t('contact.sections.visit.hours.saturday')}<br />
                                        {t('contact.sections.visit.hours.sunday')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-8 rounded-lg">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">{t('contact.sections.connect.title')}</h2>
                        <div className="space-y-6">
                            <div className="flex items-start">
                                <Phone className="mt-1 h-5 w-5 text-sky-600" />
                                <div className="ml-4">
                                    <p className="text-lg font-medium text-gray-900">{t('contact.sections.connect.phone.title')}</p>
                                    <p className="mt-1 text-gray-600">{t('contact.sections.connect.phone.primary')}</p>
                                    <p className="mt-1 text-gray-600 hidden">{t('contact.sections.connect.phone.secondary')}</p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <Mail className="mt-1 h-5 w-5 text-sky-600" />
                                <div className="ml-4">
                                    <p className="text-lg font-medium text-gray-900">{t('contact.sections.connect.email.title')}</p>
                                    <p className="mt-1 text-gray-600">{t('contact.sections.connect.email.primary')}</p>
                                    <p className="mt-1 text-gray-600 hidden">{t('contact.sections.connect.email.secondary')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Team Members Section */}
            <div className="bg-white py-24">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
                            {t('contact.sections.team.title')}
                        </h2>
                        <p className="mt-4 text-lg text-gray-600">
                            {t('contact.sections.team.subtitle')}
                        </p>
                    </div>
                    <ul className="mx-auto mt-20 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:max-w-4xl">
                        {people.map((person) => (
                            <li key={person.name} className="flex flex-col gap-6 xl:flex-row">
                                <img
                                    src={person.imageUrl}
                                    alt={person.name}
                                    className="aspect-[4/5] w-52 flex-none rounded-2xl object-cover"
                                />
                                <div className="flex-auto">
                                    <h3 className="text-lg font-semibold text-gray-900">{person.name}</h3>
                                    <p className="text-base mt-1 text-gray-500">{person.role}</p>
                                    <p className="mt-2 text-sm text-sky-600"><a href={'mailto:'+ person.email}>{t('contact.sections.connect.email.title')}</a></p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Contact Form Section */}
            <div className="bg-gray-50 py-24 sm:py-32">
                <div className="mx-auto max-w-2xl px-6 lg:px-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-semibold text-gray-900">{t('contact.sections.form.title')}</h2>
                        <p className="mt-4 text-lg text-gray-600">
                            {t('contact.sections.form.subtitle')}
                        </p>
                    </div>
                    
                    {/* Success Message */}
                    {submitStatus === 'success' && (
                        <div className="mt-8 rounded-md bg-green-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-green-800">
                                        {t('contact.sections.form.success')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {submitStatus === 'error' && (
                        <div className="mt-8 rounded-md bg-red-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-red-800">
                                        {t('contact.sections.form.error')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="mt-12">
                        <div className="space-y-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                    {t('contact.sections.form.fields.name.label')}
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder={t('contact.sections.form.fields.name.placeholder')}
                                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-600 focus:ring-sky-600 sm:text-sm"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    {t('contact.sections.form.fields.email.label')}
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder={t('contact.sections.form.fields.email.placeholder')}
                                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-600 focus:ring-sky-600 sm:text-sm"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                    {t('contact.sections.form.fields.phone.label')}
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    id="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder={t('contact.sections.form.fields.phone.placeholder')}
                                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-600 focus:ring-sky-600 sm:text-sm"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                                    {t('contact.sections.form.fields.message.label')}
                                </label>
                                <textarea
                                    name="message"
                                    id="message"
                                    rows={6}
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder={t('contact.sections.form.fields.message.placeholder')}
                                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-600 focus:ring-sky-600 sm:text-sm"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    className="w-full rounded-md bg-sky-600 py-3 px-4 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-600 focus:ring-offset-2 disabled:opacity-50"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting 
                                        ? t('estate.details.actions.sending', 'Sending...') 
                                        : t('contact.sections.form.submit')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;